<template>
  <v-container class="justify-center pa-0">
    <v-card flat width="800px" class="rounded-card mx-auto">
      <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">
        {{ snackbarText }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </template>
      </v-snackbar>

      <v-skeleton-loader v-if="loading" type="list-item@12" width="100%"></v-skeleton-loader>

      <v-form v-else v-model="valid" ref="form" lazy-validation :disabled="saving" @submit.prevent="save">
        <v-card-title class="text-h6">{{ $t('landingSettings.textsSectionTitle') }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" class="pb-0">
              <v-text-field
                v-model="eyebrow"
                outlined
                :label="$t('landingSettings.eyebrow')"
                :placeholder="$t('landingSettings.eyebrowPlaceholder')"
                :counter="60"
                maxlength="60"
              ></v-text-field>
            </v-col>
            <v-col cols="12" class="py-0">
              <v-text-field
                v-model="title"
                outlined
                :label="$t('landingSettings.title')"
                :placeholder="$t('landingSettings.titlePlaceholder')"
                :counter="120"
                maxlength="120"
              ></v-text-field>
            </v-col>
            <v-col cols="12" class="py-0">
              <v-textarea
                v-model="subheader"
                outlined
                auto-grow
                rows="2"
                :label="$t('landingSettings.subheader')"
                :placeholder="$t('landingSettings.subheaderPlaceholder')"
                :counter="250"
                maxlength="250"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-title class="text-h6">{{ $t('landingSettings.menuSectionTitle') }}</v-card-title>
        <v-card-text>
          <v-switch
            v-model="showMenu"
            :label="$t('landingSettings.showMenu')"
            class="mt-0"
          ></v-switch>

          <template v-if="showMenu">
            <v-radio-group v-model="menuMode" row class="mt-0">
              <v-radio :label="$t('landingSettings.modeDays')" value="days" on-icon="fas fa-dot-circle" off-icon="far fa-circle"></v-radio>
              <v-radio :label="$t('landingSettings.modeWeek')" value="week" on-icon="fas fa-dot-circle" off-icon="far fa-circle"></v-radio>
            </v-radio-group>

            <v-row v-if="menuMode === 'days'">
              <v-col cols="12" sm="4" class="py-0">
                <v-text-field
                  v-model.number="daysCount"
                  outlined
                  type="number"
                  min="1"
                  max="14"
                  :label="$t('landingSettings.daysCount')"
                  :rules="[daysCountRule]"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row v-if="menuMode === 'week'">
              <v-col cols="12" class="py-0">
                <v-select
                  v-model="weekDays"
                  outlined
                  multiple
                  chips
                  small-chips
                  :items="weekDayOptions"
                  item-text="label"
                  item-value="value"
                  :label="$t('landingSettings.weekDays')"
                  :rules="[weekDaysRule]"
                  append-icon="fas fa-chevron-down"
                >
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item v-on="on" v-bind="attrs">
                      <v-list-item-action>
                        <v-checkbox :input-value="attrs.inputValue" on-icon="fas fa-check-square" off-icon="far fa-square"></v-checkbox>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.label }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <v-switch
              v-model="showEmptyDays"
              :label="$t('landingSettings.showEmptyDays')"
              hide-details
            ></v-switch>
          </template>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-title class="text-h6">{{ $t('landingSettings.registrationSectionTitle') }}</v-card-title>
        <v-card-text>
          <v-switch
            v-model="showRegistration"
            :label="$t('landingSettings.showRegistration')"
            class="mt-0"
          ></v-switch>

          <v-select
            v-if="showRegistration"
            v-model="registrationSmartLinkId"
            outlined
            :items="smartLinkOptions"
            :label="$t('landingSettings.registrationLink')"
            append-icon="fas fa-chevron-down"
            hide-details
          >
            <template v-slot:item="{ item, attrs, on }">
              <v-list-item v-on="on" v-bind="attrs">
                <v-list-item-icon v-if="item.icon">
                  <v-icon small>{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.subtitle">{{ item.subtitle }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="item.inactive">
                  <v-chip x-small outlined>{{ $t('landingSettings.inactiveLink') }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-select>
        </v-card-text>

        <v-card-actions>
          <span class="text-caption grey--text pl-2">{{ $t('landingSettings.cacheHint') }}</span>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            type="submit"
            :loading="saving"
            :disabled="!valid || !isValid"
          >{{ $t('landingSettings.saveButton') }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import * as actionTypes from '@/store/action-types'

@Component
export default class LandingPageSettings extends Vue {
  eyebrow = ''
  title = ''
  subheader = ''
  showMenu = true
  showRegistration = true
  menuMode: 'days' | 'week' = 'days'
  daysCount = 7
  weekDays: number[] = [1, 2, 3, 4, 5]
  showEmptyDays = false
  registrationSmartLinkId: string | null = null

  smartLinks: any[] = []
  loading = true
  valid = true
  saving = false
  snackbar = false
  snackbarText = ''
  snackbarColor = 'success'

  get weekDayOptions () {
    const labels: string[] = this.$t('landingSettings.weekDayShort') as any
    return [1, 2, 3, 4, 5, 6, 7].map(value => ({ value, label: labels[value - 1] }))
  }

  get smartLinkOptions () {
    const now = new Date()
    const options: any[] = [
      { text: this.$t('landingSettings.automaticLink'), value: null, icon: 'fas fa-magic' },
      { text: this.$t('landingSettings.noLink'), value: 'none', icon: 'fas fa-ban' },
      { divider: true }
    ]
    for (const link of this.smartLinks) {
      const description = link.configuration?.[0]?.description
      const inactive = new Date(link.activeUntil) < now || new Date(link.activeFrom) > now
      options.push({
        text: description || link.slug,
        subtitle: description ? link.slug : undefined,
        inactive,
        icon: 'fas fa-link',
        value: link.id
      })
    }
    return options
  }

  get isValid (): boolean {
    if (this.menuMode === 'days' && this.daysCountRule(this.daysCount) !== true) return false
    if (this.menuMode === 'week' && this.weekDays.length === 0) return false
    return true
  }

  weekDaysRule (value: number[]): boolean | string {
    return value.length > 0 ? true : this.$t('landingSettings.weekDaysRule') as string
  }

  daysCountRule (value: number): boolean | string {
    return (Number.isInteger(value) && value >= 1 && value <= 14)
      ? true
      : this.$t('landingSettings.daysCountRule') as string
  }

  async mounted () {
    const [config, links] = await Promise.all([
      this.$store.dispatch(actionTypes.FETCH_LANDING_PAGE_CONFIG),
      this.$store.dispatch(actionTypes.FETCH_SMARTLINKS)
    ])
    this.smartLinks = links

    this.eyebrow = config.eyebrow ?? ''
    this.title = config.title ?? ''
    this.subheader = config.subheader ?? ''
    this.showMenu = config.showMenu !== false
    this.showRegistration = config.showRegistration !== false
    const menu = config.menu ?? {}
    if (menu.mode === 'week') this.menuMode = 'week'
    if (Number.isInteger(menu.daysCount)) this.daysCount = menu.daysCount
    if (Array.isArray(menu.weekDays) && menu.weekDays.length > 0) this.weekDays = menu.weekDays
    this.showEmptyDays = menu.showEmptyDays === true
    this.registrationSmartLinkId = config.registrationSmartLinkId ?? null
    this.loading = false
  }

  async save () {
    this.saving = true
    try {
      await this.$store.dispatch(actionTypes.UPDATE_LANDING_PAGE_CONFIG, {
        eyebrow: this.eyebrow.trim() || null,
        title: this.title.trim() || null,
        subheader: this.subheader.trim() || null,
        showMenu: this.showMenu,
        showRegistration: this.showRegistration,
        menu: {
          mode: this.menuMode,
          daysCount: this.daysCount,
          weekDays: [...this.weekDays].sort((a, b) => a - b),
          showEmptyDays: this.showEmptyDays
        },
        registrationSmartLinkId: this.registrationSmartLinkId
      })
      this.snackbarColor = 'success'
      this.snackbarText = this.$t('landingSettings.saved') as string
    } catch (e) {
      this.snackbarColor = 'error'
      this.snackbarText = this.$t('landingSettings.saveFailed') as string
    } finally {
      this.saving = false
      this.snackbar = true
    }
  }
}
</script>
